import { G } from '@mobily/ts-belt';
import { Box, FormHelperTextProps, SxProps, Typography } from '@mui/material';
import { maxValue, minValue, Validator } from 'react-admin';
import { useFormContext } from 'react-hook-form';

import { generatePlanTypeLabeled } from '@Common/bo/b2b_campaigns';

import { AmountInput, MaxButton } from '@sh4res/bo-common/fields';
import { inputDisabledProps } from '@sh4res/bo-common/utils';

type Props = {
  allocationId: string;
  choiceIndex: number;
  editionDisabled: boolean;
  onMatchingContributionAmountChange: () => void;
  handleArrowKeyNavigation: React.KeyboardEventHandler<HTMLInputElement>;
  maxAmount?: number | null;
  FormHelperTextProps: Partial<FormHelperTextProps>;
  inputSx: SxProps;
  'data-testid': string;
};

export function B2bMatchingContributionInput({
  allocationId,
  choiceIndex,
  editionDisabled,
  onMatchingContributionAmountChange,
  handleArrowKeyNavigation,
  maxAmount,
  FormHelperTextProps,
  inputSx,
  'data-testid': testId,
}: Props) {
  const { getValues, setValue } = useFormContext();

  const matchingContributionComputedByByPlanTypeLabeled = getValues(
    'matching_contribution_computed_by_by_plan_type_labeled',
  );
  const campaignType = getValues('type');

  const choicePath =
    `allocations.${allocationId}.company_collected_choices.${choiceIndex}` as const;

  const allocationType = getValues(`${choicePath}.allocation_type`);
  const planType = getValues(`${choicePath}.plan_type`);
  const planLabel = getValues(`${choicePath}.plan_label`);
  const matchingContributionAmount = getValues(`${choicePath}.matching_contribution_amount`);

  const planTypeLabeled = generatePlanTypeLabeled({ planType, planLabel });

  if (
    allocationType === 'payout' ||
    // For matching_contribution campaigns the company always provides MC amounts regardless
    // of the plan-level matching_contribution_computed_by setting.
    (campaignType !== 'matching_contribution' &&
      G.isNotNullable(planType) &&
      matchingContributionComputedByByPlanTypeLabeled[planTypeLabeled] !== 'company')
  ) {
    if (G.isNotNullable(matchingContributionAmount)) {
      setValue(`${choicePath}.matching_contribution_amount`, undefined);
    }
    return <Box flex="4" display="flex" alignItems="center" sx={{ overflowX: 'auto' }}></Box>;
  }

  const validators: Validator[] = [minValue(0)];
  if (G.isNotNullable(maxAmount)) {
    validators.push(
      maxValue(
        maxAmount,
        'resources.bo_common.b2b.campaigns.sections.allocations.errors.ceiling_reached',
      ),
    );
  }

  return (
    <Box
      flex="4"
      display="flex"
      alignItems="center"
      sx={{ overflowX: 'auto', pb: '16px', pt: '8px' }}>
      {allocationType !== 'payout' && (
        <>
          <AmountInput
            source={`${choicePath}.matching_contribution_amount`}
            label={false}
            validate={validators}
            min={0}
            onBlur={onMatchingContributionAmountChange}
            onKeyDown={handleArrowKeyNavigation}
            FormHelperTextProps={FormHelperTextProps}
            testId={testId}
            {...inputDisabledProps(editionDisabled, inputSx)}
          />
          {G.isNotNullable(maxAmount) && (
            <MaxButton
              field={`${choicePath}.matching_contribution_amount`}
              value={maxAmount}
              disabled={editionDisabled}
              data-testid={`${testId}_MaxButton`}
              onAfterSet={onMatchingContributionAmountChange}
            />
          )}
          <Typography ml={1}>€</Typography>
        </>
      )}
    </Box>
  );
}
